"use client"

import { useAutoAnimate } from "@formkit/auto-animate/react"
import clsx from "clsx"
import { useEffect, useRef, useState } from "react"
import { extractAndRecordCouponErrorMessage } from "src/checkout/hooks/extractCouponErrorMessage"
import { addCouponOrThrow } from "src/checkout/hooks/useCart"
import { useCoupons } from "src/checkout/hooks/useCoupon"
import { NpInput } from "src/components/np-design/components/input/NpInput"
import { NpButton } from "src/components/np-design/components/NpButton"
import { NpErrorMessage } from "src/components/np-design/components/NpErrorMessage"
import { NpIcon } from "src/components/np-design/components/NpIcon"
import { useEventCallback } from "src/hooks/useEventCallback"

export const CouponRegion = () => {
  const coupons = useCoupons()
  const [expanded, setExpanded] = useState(false)
  const ref = useRef<HTMLInputElement>(null)
  const [value, setValue] = useState("")
  const [adding, setAdding] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [animationParent] = useAutoAnimate()

  useEffect(() => {
    if (coupons) {
      setExpanded(false)
      setValue("")
      setAdding(false)
      setDeleting(false)
      setErrorMessage("")
    }
  }, [coupons])

  const tryAddCoupon = useEventCallback(async () => {
    if (!value) {
      setErrorMessage("")
      return
    }

    setAdding(true)
    setErrorMessage("")

    try {
      await addCouponOrThrow(value)
    } catch (error) {
      const message = extractAndRecordCouponErrorMessage(value, error)
      setErrorMessage(message)
    } finally {
      setAdding(false)
    }
  })

  return (
    <div ref={animationParent} className="mx-auto flex w-75 max-w-full flex-col gap-2 text-center">
      <NpButton
        color="gray75"
        size="sm"
        variant="ghost"
        disabled={adding || deleting}
        onClick={() => setExpanded(!expanded)}
      >
        <NpIcon name="percent" />
        <span>Gutschein-Code eingeben</span>
        <NpIcon
          name="keyboard_arrow_down"
          className={clsx("transform transition-transform", {
            "rotate-180": expanded,
          })}
        />
      </NpButton>

      {expanded && (
        <>
          <div className="relative">
            <NpInput
              ref={ref}
              placeholder="Gutschein-Code"
              autoComplete="off"
              value={value}
              onChange={(value) => {
                setValue(value)
                setErrorMessage("")
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  tryAddCoupon()
                }
              }}
            />
          </div>

          <NpButton size="sm" onClick={tryAddCoupon} loading={adding}>
            Aktivieren
          </NpButton>

          {errorMessage && <NpErrorMessage message={errorMessage} />}
        </>
      )}
    </div>
  )
}
